import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiLayers,
  FiTarget,
  FiInfo,
  FiCheckCircle,
  FiTrendingUp,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const passageComponents = [
  {
    id: 1,
    name: 'The Point (Main Idea)',
    icon: <FiTarget />,
    description:
      'The central argument or thesis that the author wants to convey.',
    characteristics: [
      'Usually found in introduction or conclusion',
      'Answers "What is the author trying to prove?"',
      'May be stated explicitly or implied',
      'All other components support this point',
    ],
    examples: [
      'Climate change is accelerating faster than previously predicted.',
      'Modern art movements reflect social upheaval of their time.',
      'Traditional economic models fail to account for human psychology.',
    ],
    identificationTips: [
      'Look for thesis statements in first/last paragraphs',
      'Watch for phrases like "I argue that..." or "This shows that..."',
      'Ask yourself: "What does the author want me to believe?"',
      'The point should encompass the entire passage, not just one part',
    ],
  },
  {
    id: 2,
    name: 'Background (Context)',
    icon: <FiInfo />,
    description:
      'Information that sets up the discussion and provides necessary context.',
    characteristics: [
      'Usually appears early in the passage',
      'Provides historical context or definitions',
      'Sets the stage for the main argument',
      'Often includes widely accepted facts',
    ],
    examples: [
      'For decades, scientists have studied climate patterns...',
      'The Industrial Revolution began in Britain in the late 18th century...',
      'Behavioral economics emerged in the 1970s as a field of study...',
    ],
    identificationTips: [
      'Often contains phrases like "Traditionally," "For years," "Historically"',
      'Provides setup information before getting to the main point',
      'Usually not controversial or debatable',
      'Answers questions like "What\'s the situation?" or "What\'s the context?"',
    ],
  },
  {
    id: 3,
    name: 'Support (Evidence)',
    icon: <FiCheckCircle />,
    description:
      'Evidence, examples, and reasoning that backs up the main point.',
    characteristics: [
      'Provides proof for the main argument',
      'Can include statistics, studies, examples, or logical reasoning',
      'Directly connects to and reinforces the main point',
      'Often the bulk of the passage content',
    ],
    examples: [
      'Studies show that 90% of climate scientists agree...',
      'For example, Picasso\'s "Guernica" was painted in response to...',
      'Research by Kahneman and Tversky demonstrated that...',
    ],
    identificationTips: [
      'Look for evidence words: "studies show," "research indicates," "for example"',
      'Ask "How does this support the main point?"',
      'Often includes specific data, quotes, or concrete examples',
      'Should logically connect to the main argument',
    ],
  },
  {
    id: 4,
    name: 'Implications (Consequences)',
    icon: <FiTrendingUp />,
    description:
      'What the main point means for the future or its broader significance.',
    characteristics: [
      'Usually appears at the end of passages',
      'Discusses future consequences or broader meaning',
      'May suggest solutions or next steps',
      'Answers "So what?" or "What does this mean?"',
    ],
    examples: [
      'These findings suggest that current climate policies are insufficient...',
      'This artistic movement influenced later developments in...',
      'These insights could revolutionize how we approach economic policy...',
    ],
    identificationTips: [
      'Watch for future-oriented language: "will," "should," "suggests that"',
      'Often in concluding paragraphs',
      'Discusses broader significance or consequences',
      'May propose solutions or call for action',
    ],
  },
];

const practicePassages = [
  {
    id: 1,
    title: 'Practice Passage 1: Identifying Components',
    text: `For over a century, antibiotics have been considered one of medicine's greatest achievements, saving countless lives from bacterial infections. The discovery of penicillin by Alexander Fleming in 1928 marked the beginning of the antibiotic era, revolutionizing medical treatment worldwide.

However, the widespread and often inappropriate use of antibiotics has led to a serious unintended consequence: antibiotic resistance. Bacteria evolve rapidly, and exposure to antibiotics creates selective pressure that favors resistant strains. Recent studies indicate that antibiotic-resistant infections cause over 700,000 deaths globally each year, and this number is projected to rise dramatically.

The development of antibiotic resistance represents a critical threat to modern medicine. If current trends continue, routine medical procedures could become life-threatening due to untreatable infections. This crisis demands immediate action, including stricter antibiotic prescribing guidelines and increased investment in developing new antimicrobial treatments.`,
    components: {
      background:
        "For over a century, antibiotics have been considered one of medicine's greatest achievements, saving countless lives from bacterial infections. The discovery of penicillin by Alexander Fleming in 1928 marked the beginning of the antibiotic era, revolutionizing medical treatment worldwide.",
      point:
        'The development of antibiotic resistance represents a critical threat to modern medicine.',
      support:
        'However, the widespread and often inappropriate use of antibiotics has led to a serious unintended consequence: antibiotic resistance. Bacteria evolve rapidly, and exposure to antibiotics creates selective pressure that favors resistant strains. Recent studies indicate that antibiotic-resistant infections cause over 700,000 deaths globally each year, and this number is projected to rise dramatically.',
      implications:
        'If current trends continue, routine medical procedures could become life-threatening due to untreatable infections. This crisis demands immediate action, including stricter antibiotic prescribing guidelines and increased investment in developing new antimicrobial treatments.',
    },
  },
  {
    id: 2,
    title: 'Practice Passage 2: Complex Structure',
    text: `The field of artificial intelligence has experienced remarkable growth since its inception in the 1950s. Early pioneers like Alan Turing and John McCarthy envisioned machines that could think and reason like humans, laying the theoretical groundwork for what would become a transformative technology.

Recent breakthroughs in machine learning, particularly deep learning algorithms, have enabled AI systems to perform tasks that were once thought impossible for machines. These systems can now recognize images with superhuman accuracy, translate languages in real-time, and even generate creative content. Major technology companies have invested billions of dollars in AI research, leading to rapid advancement in the field.

Despite these impressive achievements, current AI systems remain fundamentally limited. They excel at narrow, specific tasks but lack the general intelligence and reasoning capabilities of humans. Moreover, these systems often function as "black boxes," making decisions through processes that are opaque and difficult to interpret.

The future development of artificial general intelligence (AGI) could fundamentally alter human society. While AGI promises to solve complex global challenges like climate change and disease, it also raises profound questions about employment, privacy, and human autonomy. Ensuring that AI development proceeds safely and beneficially will require unprecedented international cooperation and careful regulation.`,
    components: {
      background:
        'The field of artificial intelligence has experienced remarkable growth since its inception in the 1950s. Early pioneers like Alan Turing and John McCarthy envisioned machines that could think and reason like humans, laying the theoretical groundwork for what would become a transformative technology.',
      point:
        'While AI has made impressive progress, current systems have significant limitations, and future AGI development will require careful management.',
      support:
        'Recent breakthroughs in machine learning, particularly deep learning algorithms, have enabled AI systems to perform tasks that were once thought impossible for machines... Despite these impressive achievements, current AI systems remain fundamentally limited. They excel at narrow, specific tasks but lack the general intelligence and reasoning capabilities of humans.',
      implications:
        'The future development of artificial general intelligence (AGI) could fundamentally alter human society. While AGI promises to solve complex global challenges like climate change and disease, it also raises profound questions about employment, privacy, and human autonomy. Ensuring that AI development proceeds safely and beneficially will require unprecedented international cooperation and careful regulation.',
    },
  },
];

export default function PassageComponentsLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [identifiedComponents, setIdentifiedComponents] = useState({});
  const [showAnswer, setShowAnswer] = useState({});

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleComponentIdentification = (
    passageId,
    componentType,
    userAnswer
  ) => {
    setIdentifiedComponents((prev) => ({
      ...prev,
      [`${passageId}-${componentType}`]: userAnswer,
    }));
  };

  const checkAnswer = (passageId, componentType) => {
    setShowAnswer((prev) => ({
      ...prev,
      [`${passageId}-${componentType}`]: true,
    }));
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">
        The Four Building Blocks of Every Passage
      </h2>

      <div className="lesson-intro">
        <p>
          Every GRE passage contains these four essential components. Learning
          to identify them quickly will help you understand the passage
          structure and answer questions more effectively.
        </p>
      </div>

      <div className="question-types-grid">
        {passageComponents.map((component) => (
          <div
            key={component.id}
            onClick={() => setSelectedComponent(component)}
            className={`question-type-card ${selectedComponent?.id === component.id ? 'selected' : ''}`}
          >
            <div className="type-header">
              <div className="type-number">{component.icon}</div>
              <h3 className="type-title">{component.name}</h3>
            </div>
            <p className="type-description">{component.description}</p>
          </div>
        ))}
      </div>

      {selectedComponent && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            {selectedComponent.icon}
            {selectedComponent.name}
          </h3>

          <div className="analysis-section">
            <h4>Key Characteristics:</h4>
            <ul className="tips-list">
              {selectedComponent.characteristics.map((char, index) => (
                <li key={index}>{char}</li>
              ))}
            </ul>
          </div>

          <div className="analysis-section">
            <h4>Example Phrases:</h4>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {selectedComponent.examples.map((example, index) => (
                <div
                  key={index}
                  style={{
                    background: 'var(--liwaza-surface-container-highest)',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontStyle: 'italic',
                    color: 'var(--liwaza-on-surface)',
                    fontSize: '0.9rem',
                  }}
                >
                  "{example}"
                </div>
              ))}
            </div>
          </div>

          <div className="analysis-section">
            <h4 className="tips-header">How to Identify:</h4>
            <ul className="tips-list">
              {selectedComponent.identificationTips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice: Identify Passage Components</h2>

      {practicePassages.map((passage) => (
        <div key={passage.id} className="practice-passage">
          <h3 className="passage-title">{passage.title}</h3>

          <div className="passage-text">
            {passage.text.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h4
              style={{
                color: 'var(--liwaza-on-surface)',
                marginBottom: '1rem',
              }}
            >
              Identify the Components:
            </h4>

            {['background', 'point', 'support', 'implications'].map(
              (componentType) => (
                <div key={componentType} style={{ marginBottom: '1.5rem' }}>
                  <label
                    style={{
                      display: 'block',
                      color: 'var(--liwaza-on-surface)',
                      marginBottom: '0.5rem',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {componentType === 'point'
                      ? 'The Point (Main Idea)'
                      : componentType === 'background'
                        ? 'Background (Context)'
                        : componentType === 'support'
                          ? 'Support (Evidence)'
                          : 'Implications (Consequences)'}
                    :
                  </label>

                  <textarea
                    value={
                      identifiedComponents[`${passage.id}-${componentType}`] ||
                      ''
                    }
                    onChange={(e) =>
                      handleComponentIdentification(
                        passage.id,
                        componentType,
                        e.target.value
                      )
                    }
                    placeholder={`Identify the ${componentType} in the passage...`}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid var(--liwaza-outline-variant)',
                      background: 'var(--liwaza-surface-container-highest)',
                      color: 'var(--liwaza-on-surface)',
                      fontSize: '0.9rem',
                      resize: 'vertical',
                    }}
                  />

                  <div
                    style={{
                      marginTop: '0.5rem',
                      display: 'flex',
                      gap: '0.5rem',
                    }}
                  >
                    <button
                      onClick={() => checkAnswer(passage.id, componentType)}
                      className="show-explanation-btn"
                    >
                      Check Answer
                    </button>
                  </div>

                  {showAnswer[`${passage.id}-${componentType}`] && (
                    <div className="explanation-box">
                      <h5
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          marginBottom: '0.5rem',
                          fontSize: '0.9rem',
                        }}
                      >
                        Correct Answer:
                      </h5>
                      <p
                        style={{
                          color: 'var(--liwaza-on-primary-container)',
                          fontSize: '0.9rem',
                          lineHeight: 1.5,
                          margin: 0,
                          fontStyle: 'italic',
                        }}
                      >
                        {passage.components[componentType]}
                      </p>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
          <button
            onClick={() =>
              navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
            }
            className="close-lesson-button"
          >
            <FiHome /> Close Lesson
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiLayers />
          Lesson 3: Identify Passage Components
        </h1>
        <p className="lesson-subtitle">
          Learn to quickly identify the four building blocks that form every GRE
          passage: The Point, Background, Support, and Implications. This skill
          is essential for understanding passage structure and answering
          questions accurately.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help identifying Passage Components?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding how to identify Passage Components in GRE Reading Comprehension"
          aicontext="Every GRE passage contains these four essential components: The Point, Background, Support, and Implications. Learning to identify them quickly will help you understand the passage structure and answer questions more effectively."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate(
              '/exams/gre/reading-comprehension/lesson/five-step-process'
            )
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: 5-Step Process
        </button>

        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/chunk-reading')
          }
          className="next-lesson-button"
        >
          Next: Chunk Reading <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
